import { useState } from "react";
import { api } from "@/lib/api";
import { Link } from "@tanstack/react-router";
import { SEO } from "@/components/SEO";
import { DKIMResult } from "@/components/tool-results";

export function DKIMCheckerPage() {
  const [domain, setDomain] = useState("");
  const [selector, setSelector] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const runCheck = async () => {
    if (!domain) return;
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      let data;
      if (!selector) {
        data = await api.tools.discoverDKIM(domain);
      } else {
        data = await api.tools.validateDKIM(domain, selector);
      }
      setResult(data);
    } catch (err: any) {
      setError(err.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "Free DKIM Record Checker",
    "description": "Validate your DKIM record and discover DKIM selectors. Check DKIM configuration for Google, Microsoft 365, and other email providers.",
    "url": "https://getsent.dev/tools/dkim-checker",
    "applicationCategory": "SecurityApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What is a DKIM record?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "DKIM (DomainKeys Identified Mail) is an email authentication method that uses cryptographic signatures to verify that an email was sent by an authorized server and hasn't been modified in transit. The public key is published as a DNS TXT record."
        }
      },
      {
        "@type": "Question",
        "name": "What is a DKIM selector?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "A DKIM selector is a string that identifies a specific DKIM key. It's used to look up the public key in DNS. Common selectors include 'google' for Google Workspace, 'selector1' and 'selector2' for Microsoft 365, and 's1' or 's2' for various email services."
        }
      },
      {
        "@type": "Question",
        "name": "How do I find my DKIM selector?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "You can find your DKIM selector in your email provider's admin console, or by examining the headers of an email sent from your domain. Look for the 's=' tag in the DKIM-Signature header. Our tool can also auto-discover common selectors."
        }
      }
    ]
  };

  return (
    <div>
      <SEO
        title="Free DKIM Record Checker - Validate Your DKIM Configuration"
        description="Check your DKIM record and discover DKIM selectors. Validate DKIM for Google Workspace, Microsoft 365, and other providers. No signup required."
        canonical="/tools/dkim-checker"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      {/* Hero */}
      <section className="relative overflow-hidden border-b bg-gradient-to-b from-purple-50/50 to-transparent py-16 dark:from-purple-950/20 sm:py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-purple-500/10 px-4 py-1.5 text-sm font-medium text-purple-600">
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" />
              </svg>
              Free Tool
            </div>
            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl">
              DKIM Record{" "}
              <span className="bg-gradient-to-r from-purple-500 to-pink-600 bg-clip-text text-transparent">
                Checker
              </span>
            </h1>
            <p className="mt-4 text-lg text-muted-foreground">
              Validate your DKIM configuration and discover DKIM selectors.
              Verify cryptographic signatures for email authentication.
            </p>
          </div>
        </div>
      </section>

      {/* Tool */}
      <section className="py-12 sm:py-16">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <div className="rounded-2xl border bg-card p-6 shadow-lg">
            <div className="flex flex-col gap-4 sm:flex-row">
              <div className="flex-1">
                <label className="mb-2 block text-sm font-medium">Enter your domain</label>
                <input
                  type="text"
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                  placeholder="example.com"
                  className="w-full rounded-lg border bg-background px-4 py-3 text-base focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                  onKeyDown={(e) => e.key === "Enter" && runCheck()}
                />
              </div>
              <div className="sm:w-48">
                <label className="mb-2 block text-sm font-medium">
                  Selector <span className="text-muted-foreground">(optional)</span>
                </label>
                <input
                  type="text"
                  value={selector}
                  onChange={(e) => setSelector(e.target.value)}
                  placeholder="google, selector1"
                  className="w-full rounded-lg border bg-background px-4 py-3 text-base focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                />
              </div>
              <div className="flex items-end">
                <button
                  onClick={runCheck}
                  disabled={loading || !domain}
                  className="w-full rounded-lg bg-gradient-to-r from-purple-500 to-pink-600 px-8 py-3 font-semibold text-white shadow-lg shadow-purple-500/25 transition-all hover:shadow-xl hover:shadow-purple-500/30 disabled:opacity-50 sm:w-auto"
                >
                  {loading ? "Checking..." : "Check DKIM"}
                </button>
              </div>
            </div>
            <p className="mt-3 text-sm text-muted-foreground">
              Leave selector blank to auto-discover common DKIM selectors (Google, Microsoft, etc.)
            </p>
          </div>

          {error && (
            <div className="mt-6 rounded-lg border border-red-500/20 bg-red-500/10 p-4 text-red-600">
              {error}
            </div>
          )}

          {result && (
            <div className="mt-6">
              <DKIMResult data={result} />
            </div>
          )}
        </div>
      </section>

      {/* What is DKIM */}
      <section className="border-t py-12 sm:py-16">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <h2 className="text-2xl font-bold">What is DKIM?</h2>
          <div className="mt-4 space-y-4 text-muted-foreground">
            <p>
              <strong className="text-foreground">DKIM (DomainKeys Identified Mail)</strong> is an email
              authentication method that adds a digital signature to outgoing emails. This signature
              allows receiving servers to verify that the email was actually sent by an authorized
              server and hasn't been tampered with in transit.
            </p>
            <p>
              A DKIM record is a DNS TXT record that contains the public key. The sending server
              signs emails with the private key, and receivers use the public key to verify the signature.
            </p>
            <code className="block rounded-lg bg-muted p-4 text-sm text-foreground">
              google._domainkey.example.com TXT "v=DKIM1; k=rsa; p=MIIBIjANBg..."
            </code>
          </div>
        </div>
      </section>

      {/* Common Selectors */}
      <section className="border-t py-12 sm:py-16">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <h2 className="text-2xl font-bold">Common DKIM Selectors by Provider</h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <div className="rounded-xl border bg-card p-5">
              <h3 className="font-semibold">Google Workspace</h3>
              <code className="mt-2 block text-sm text-muted-foreground">google</code>
            </div>
            <div className="rounded-xl border bg-card p-5">
              <h3 className="font-semibold">Microsoft 365</h3>
              <code className="mt-2 block text-sm text-muted-foreground">selector1, selector2</code>
            </div>
            <div className="rounded-xl border bg-card p-5">
              <h3 className="font-semibold">Mailchimp</h3>
              <code className="mt-2 block text-sm text-muted-foreground">k1, k2, k3</code>
            </div>
            <div className="rounded-xl border bg-card p-5">
              <h3 className="font-semibold">SendGrid</h3>
              <code className="mt-2 block text-sm text-muted-foreground">s1, s2</code>
            </div>
            <div className="rounded-xl border bg-card p-5">
              <h3 className="font-semibold">Amazon SES</h3>
              <code className="mt-2 block text-sm text-muted-foreground">Various (check SES console)</code>
            </div>
            <div className="rounded-xl border bg-card p-5">
              <h3 className="font-semibold">HubSpot</h3>
              <code className="mt-2 block text-sm text-muted-foreground">hs1, hs2</code>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t py-12 sm:py-16">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <h2 className="text-2xl font-bold">Frequently Asked Questions</h2>
          <div className="mt-6 space-y-6">
            <div>
              <h3 className="font-semibold">What is a DKIM record?</h3>
              <p className="mt-2 text-muted-foreground">
                DKIM (DomainKeys Identified Mail) is an email authentication method that uses
                cryptographic signatures to verify that an email was sent by an authorized server
                and hasn't been modified in transit. The public key is published as a DNS TXT record.
              </p>
            </div>
            <div>
              <h3 className="font-semibold">What is a DKIM selector?</h3>
              <p className="mt-2 text-muted-foreground">
                A DKIM selector is a string that identifies a specific DKIM key. It's used to look
                up the public key in DNS. Common selectors include "google" for Google Workspace,
                "selector1" and "selector2" for Microsoft 365, and "s1" or "s2" for various email services.
              </p>
            </div>
            <div>
              <h3 className="font-semibold">How do I find my DKIM selector?</h3>
              <p className="mt-2 text-muted-foreground">
                You can find your DKIM selector in your email provider's admin console, or by
                examining the headers of an email sent from your domain. Look for the <code>s=</code>
                tag in the DKIM-Signature header. Our tool can also auto-discover common selectors.
              </p>
            </div>
            <div>
              <h3 className="font-semibold">Can I have multiple DKIM records?</h3>
              <p className="mt-2 text-muted-foreground">
                Yes! Unlike SPF, you can have multiple DKIM records for different selectors.
                This allows different services (like Google Workspace and Mailchimp) to each
                sign emails with their own key.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Related Tools */}
      <section className="border-t py-12 sm:py-16">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-center">Related Tools</h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <Link
              to="/tools/spf-checker"
              className="group rounded-xl border bg-card p-5 transition-all hover:border-purple-500/50 hover:shadow-lg"
            >
              <h3 className="font-semibold group-hover:text-purple-600">SPF Checker</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Validate your SPF record and check DNS lookup limits.
              </p>
            </Link>
            <Link
              to="/tools/dmarc-checker"
              className="group rounded-xl border bg-card p-5 transition-all hover:border-purple-500/50 hover:shadow-lg"
            >
              <h3 className="font-semibold group-hover:text-purple-600">DMARC Checker</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Check your DMARC policy and reporting configuration.
              </p>
            </Link>
            <Link
              to="/tools/email-authentication-checker"
              className="group rounded-xl border bg-card p-5 transition-all hover:border-purple-500/50 hover:shadow-lg"
            >
              <h3 className="font-semibold group-hover:text-purple-600">Full Email Check</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Check SPF, DKIM, and DMARC all at once.
              </p>
            </Link>
            <Link
              to="/blog/$slug"
              params={{ slug: "dkim-key-rotation-best-practices" }}
              className="group rounded-xl border bg-card p-5 transition-all hover:border-purple-500/50 hover:shadow-lg"
            >
              <h3 className="font-semibold group-hover:text-purple-600">DKIM Key Rotation</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Best practices for rotating your DKIM keys.
              </p>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t py-16">
        <div className="mx-auto max-w-7xl px-6 text-center lg:px-8">
          <h2 className="text-2xl font-bold sm:text-3xl">
            Want continuous DKIM monitoring?
          </h2>
          <p className="mt-2 text-muted-foreground">
            Get alerts when your DKIM records change or expire. Free for one domain.
          </p>
          <Link
            to="/register"
            className="mt-6 inline-flex rounded-full bg-gradient-to-r from-purple-500 to-pink-600 px-8 py-3 font-semibold text-white shadow-lg shadow-purple-500/25 transition-all hover:shadow-xl hover:shadow-purple-500/30"
          >
            Start free monitoring
          </Link>
        </div>
      </section>
    </div>
  );
}
